// src/autodesk_usage/pages/CorporatePage.tsx
// All comments in English only
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronsUpDown,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
} from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Spinner from "@/components/shared/Spinner";
import { useCorporateList } from "../hooks/useCorporateList";
import { useCorporateChildren } from "../hooks/useCorporateChildren";
import { useCorporateProductUsage } from "../hooks/useCorporateProductUsage";
import { useCompanyProductUsage } from "../hooks/useCompanyProductUsage";
import { useCompanyAccountInfo } from "../hooks/useCompanyAccountInfo";
import CorporateMonthlyUsageChart from "../components/corporate/CorporateMonthlyUsageChart";
import CompanyMonthlyUsageChart from "../components/corporate/CompanyMonthlyUsageChart";
import type { UsageMode } from "../components/corporate/ActiveLicenseUsageChart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CorporateItem, ChildCompanyItem } from "../api/corporate";

const getCorporateLabel = (corporate: CorporateItem) => {
  if (corporate.name) return corporate.name;
  if (corporate.parent_odoo_name) return corporate.parent_odoo_name;
  if (corporate.csn) return `Corporate ${corporate.csn}`;
  return "Unnamed corporate";
};

const normalizeCsn = (value?: string | null) =>
  (value ?? "").replace(/[^0-9]+/g, "");

const csnListIncludes = (csnList: string | null | undefined, target: string) => {
  const normalizedTarget = normalizeCsn(target);
  if (!normalizedTarget) return false;
  return (csnList ?? "")
    .split(",")
    .map((entry) => normalizeCsn(entry))
    .some((entry) => entry === normalizedTarget);
};

const findCompanyByCsn = (csn: string, list: CorporateItem[]) => {
  const normalizedTarget = normalizeCsn(csn);
  if (!normalizedTarget) return null;
  return (
    list.find((company) => csnListIncludes(company.csn ?? "", normalizedTarget)) ??
    null
  );
};

const findCompanyByName = (companyName: string, list: CorporateItem[]) => {
  const target = companyName.trim().toLowerCase();
  if (!target) return null;
  return (
    list.find(
      (company) =>
        company.name?.toLowerCase() === target ||
        company.parent_odoo_name?.toLowerCase() === target
    ) ?? null
  );
};

const normalizeCompanyKey = (value?: string | null) =>
  (value ?? "").trim().toLowerCase();

const monthShift = (date: Date, offset: number) =>
  new Date(date.getFullYear(), date.getMonth() + offset, 1);

const toMonthValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const defaultMonthlyRange = () => {
  const end = monthShift(new Date(), 0);
  const start = monthShift(end, -35);
  return {
    startMonth: toMonthValue(start),
    endMonth: toMonthValue(end),
  };
};

const defaultYearlyRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear() - 9, 0, 1);
  return {
    startMonth: toMonthValue(start),
    endMonth: toMonthValue(today),
  };
};

const normalizeMonthRange = (startMonth: string, endMonth: string) => {
  if (!startMonth || !endMonth) {
    return { startMonth, endMonth };
  }
  if (startMonth <= endMonth) {
    return { startMonth, endMonth };
  }
  return { startMonth: endMonth, endMonth: startMonth };
};

export default function CorporatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: corporates, isLoading, error } = useCorporateList();
  const [isCorporatePopoverOpen, setIsCorporatePopoverOpen] =
    useState<boolean>(false);
  const [corporateSearchInput, setCorporateSearchInput] =
    useState<string>("");
  const [selectedCorporateCsns, setSelectedCorporateCsns] = useState<string[]>(
    []
  );
  const [activeTab, setActiveTab] = useState<string>("corporate");
  const [timeMode, setTimeMode] = useState<UsageMode>("monthly");
  const monthlyRange = useMemo(() => defaultMonthlyRange(), []);
  const yearlyRange = useMemo(() => defaultYearlyRange(), []);
  const [customStartMonth, setCustomStartMonth] = useState<string>(
    monthlyRange.startMonth
  );
  const [customEndMonth, setCustomEndMonth] = useState<string>(
    monthlyRange.endMonth
  );
  const [sort, setSort] = useState<{
    key: "csn" | "name" | "used_users" | "active_seats";
    direction: "asc" | "desc";
  } | null>(null);

  const filteredCorporates = useMemo(() => {
    const list = corporates ?? [];
    const term = corporateSearchInput.trim().toLowerCase();
    if (!term) return list;
    const termDigits = normalizeCsn(term);
    return list.filter((item) => {
      const name =
        item.name?.toLowerCase() ?? item.parent_odoo_name?.toLowerCase() ?? "";
      const csn = item.csn?.toLowerCase() ?? "";
      const normalizedCsn = normalizeCsn(item.csn);
      const matchesName = name.includes(term);
      const matchesCsn =
        csn.includes(term) ||
        (termDigits.length > 0 && normalizedCsn.includes(termDigits));
      return matchesName || matchesCsn;
    });
  }, [corporates, corporateSearchInput]);

  const displayedCorporates = useMemo(() => {
    const selectedSet = new Set(selectedCorporateCsns);
    return [...filteredCorporates].sort((a, b) => {
      const aSelected = a.csn ? selectedSet.has(a.csn) : false;
      const bSelected = b.csn ? selectedSet.has(b.csn) : false;
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }
      const aLabel = getCorporateLabel(a).toLowerCase();
      const bLabel = getCorporateLabel(b).toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }, [filteredCorporates, selectedCorporateCsns]);

  const selectedCorporates = useMemo(() => {
    const list = corporates ?? [];
    const selectedSet = new Set(selectedCorporateCsns);
    return list.filter((corporate) => corporate.csn && selectedSet.has(corporate.csn));
  }, [corporates, selectedCorporateCsns]);

  const selectedGroupNames = useMemo(() => {
    const names = selectedCorporates
      .map((corporate) => corporate.name ?? corporate.parent_odoo_name ?? "")
      .map((name) => name.trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }, [selectedCorporates]);

  const selectedCorporate = selectedCorporates[0] ?? null;

  useEffect(() => {
    if (selectedCorporateCsns.length) {
      sessionStorage.setItem(
        "autodesk_usage_selected_corporate_csns",
        JSON.stringify(selectedCorporateCsns)
      );
      setActiveTab("corporate");
      return;
    }
    sessionStorage.removeItem("autodesk_usage_selected_corporate_csns");
    sessionStorage.removeItem("autodesk_usage_selected_corporate_csn");
  }, [selectedCorporateCsns]);

  useEffect(() => {
    if (selectedCorporateCsns.length || !(corporates ?? []).length) return;
    const storedMulti = sessionStorage.getItem(
      "autodesk_usage_selected_corporate_csns"
    );
    if (storedMulti) {
      try {
        const parsed = JSON.parse(storedMulti);
        if (Array.isArray(parsed)) {
          const next = parsed
            .map((value) => String(value ?? "").trim())
            .filter((value): value is string =>
              Boolean((corporates ?? []).some((c) => c.csn === value))
            );
          if (next.length) {
            setSelectedCorporateCsns(Array.from(new Set(next)));
            return;
          }
        }
      } catch {
        // Ignore invalid cached selection.
      }
    }

    const stored = sessionStorage.getItem(
      "autodesk_usage_selected_corporate_csn"
    );
    if (stored && (corporates ?? []).some((c) => c.csn === stored)) {
      setSelectedCorporateCsns([stored]);
    }
  }, [selectedCorporateCsns, corporates]);

  useEffect(() => {
    if (selectedCorporateCsns.length || !(corporates ?? []).length) return;
    const queryNames = Array.from(searchParams.getAll("company_name"));
    const queryCorporateNames = Array.from(searchParams.getAll("company_names"));
    const candidates = [...queryNames, ...queryCorporateNames]
      .flatMap((value) => value.split(","))
      .map((value) => value.trim())
      .filter(Boolean);
    if (!candidates.length) return;
    const matchedCsns = candidates
      .map((companyName) => findCompanyByName(companyName, corporates ?? [])?.csn)
      .filter((value): value is string => Boolean(value));
    if (matchedCsns.length) {
      setSelectedCorporateCsns(Array.from(new Set(matchedCsns)));
      setActiveTab("corporate");
    }
  }, [selectedCorporateCsns, corporates, searchParams]);

  const selectedCorporateName =
    selectedCorporates.length === 0
      ? "Corporate overview"
      : selectedCorporates.length === 1
      ? getCorporateLabel(selectedCorporates[0])
      : `${selectedCorporates.length} companies selected`;

  const selectedGroupName = selectedGroupNames[0] ?? null;

  const chartRange = useMemo(() => {
    if (timeMode === "yearly") {
      return yearlyRange;
    }
    if (timeMode === "date_range") {
      const start = customStartMonth || monthlyRange.startMonth;
      const end = customEndMonth || monthlyRange.endMonth;
      return normalizeMonthRange(start, end);
    }
    return monthlyRange;
  }, [
    customEndMonth,
    customStartMonth,
    monthlyRange,
    timeMode,
    yearlyRange,
  ]);

  const {
    data: productUsage,
    isLoading: isProductUsageLoading,
    error: productUsageError,
  } = useCorporateProductUsage(selectedGroupNames);

  const {
    data: companyProductUsage,
    isLoading: isCompanyProductUsageLoading,
    error: companyProductUsageError,
  } = useCompanyProductUsage(selectedGroupNames);

  const {
    data: accountInfo,
    isLoading: isAccountInfoLoading,
    error: accountInfoError,
  } = useCompanyAccountInfo(selectedGroupNames.length === 1 ? selectedGroupName : null);

  const {
    data: children,
    isLoading: isChildrenLoading,
    error: childrenError,
  } = useCorporateChildren(selectedGroupNames);
  const blockedCorporateSelection = useMemo(() => {
    const blockedNames = new Set<string>();
    const blockedCsns = new Set<string>();
    for (const child of children ?? []) {
      const nameKey = normalizeCompanyKey(child.name);
      const csnKey = normalizeCsn(child.csn);
      if (nameKey) blockedNames.add(nameKey);
      if (csnKey) blockedCsns.add(csnKey);
    }
    return { blockedNames, blockedCsns };
  }, [children]);
  const hasChildren = (children ?? []).length > 0;
  const showChildCompaniesTab =
    !selectedGroupNames.length || isChildrenLoading || !!childrenError || hasChildren;

  const isCorporateSelectorDisabled =
    isLoading || !!error || (corporates ?? []).length === 0;

  const isCorporateSelectionBlocked = (corporate: CorporateItem) => {
    const corporateCsn = normalizeCsn(corporate.csn);
    const corporateName = normalizeCompanyKey(
      corporate.name ?? corporate.parent_odoo_name
    );
    return (
      (corporateCsn && blockedCorporateSelection.blockedCsns.has(corporateCsn)) ||
      (corporateName && blockedCorporateSelection.blockedNames.has(corporateName))
    );
  };

  useEffect(() => {
    if (!selectedCorporateCsns.length) return;
    if (
      !blockedCorporateSelection.blockedCsns.size &&
      !blockedCorporateSelection.blockedNames.size
    ) {
      return;
    }

    const blockedSelectedCsns = new Set(
      selectedCorporates
        .filter((corporate) => isCorporateSelectionBlocked(corporate))
        .map((corporate) => corporate.csn)
        .filter((csn): csn is string => Boolean(csn))
    );

    if (blockedSelectedCsns.size === 0) return;

    setSelectedCorporateCsns((prev) =>
      prev.filter((csn) => !blockedSelectedCsns.has(csn))
    );
  }, [
    blockedCorporateSelection.blockedCsns,
    blockedCorporateSelection.blockedNames,
    selectedCorporateCsns,
    selectedCorporates,
  ]);

  const toggleCorporateSelection = (corporate: CorporateItem) => {
    if (!corporate.csn) return;
    if (isCorporateSelectionBlocked(corporate)) return;
    setSelectedCorporateCsns((prev) =>
      prev.includes(corporate.csn)
        ? prev.filter((csn) => csn !== corporate.csn)
        : [...prev, corporate.csn]
    );
  };

  const removeCorporateSelection = (corporateCsn: string) => {
    setSelectedCorporateCsns((prev) =>
      prev.filter((csn) => csn !== corporateCsn)
    );
  };

  const renderNoCorporateSelected = () => (
    <div className="flex h-36 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 text-center">
      <p className="text-sm font-medium">No company selected</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Choose one or more companies from the selector to see aggregated
        Autodesk usage.
      </p>
    </div>
  );

  const renderCorporatePlaceholder = () => (
    <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 text-center">
      <p className="text-sm font-medium">Corporate overview</p>
      <p className="max-w-md text-xs text-muted-foreground">
        Corporate insights will appear here.
      </p>
    </div>
  );

  const renderChildrenTable = (rows: ChildCompanyItem[]) => {
    const sortedRows = [...rows].sort((a, b) => {
      if (!sort) return 0;
      const dir = sort.direction === "asc" ? 1 : -1;
      if (sort.key === "csn") {
        return a.csn.localeCompare(b.csn) * dir;
      }
      if (sort.key === "used_users") {
        return (a.used_users - b.used_users) * dir;
      }
      if (sort.key === "active_seats") {
        return (a.active_seats - b.active_seats) * dir;
      }
      return a.name.localeCompare(b.name) * dir;
    });

    const handleSort = (key: "csn" | "name" | "used_users" | "active_seats") => {
      setSort((prev) => {
        if (prev && prev.key === key) {
          return {
            key,
            direction: prev.direction === "asc" ? "desc" : "asc",
          };
        }
        return { key, direction: "asc" };
      });
    };

    const renderSortIndicator = (
      key: "csn" | "name" | "used_users" | "active_seats"
    ) => {
      if (!sort || sort.key !== key) {
        return (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground ml-1 opacity-60" />
        );
      }
      return sort.direction === "asc" ? (
        <ArrowUp className="h-3 w-3 text-muted-foreground ml-1" />
      ) : (
        <ArrowDown className="h-3 w-3 text-muted-foreground ml-1" />
      );
    };

    return (
      <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-muted/60">
            <tr>
              {[
                { key: "csn", label: "CSN" },
                { key: "name", label: "Company name" },
                { key: "used_users", label: "Used users" },
                { key: "active_seats", label: "Active seats" },
              ].map(({ key, label }) => (
                <th
                  key={label}
                  className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/50 text-left"
                >
                  <button
                    type="button"
                    onClick={() =>
                      handleSort(
                        key as "csn" | "name" | "used_users" | "active_seats"
                      )
                    }
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    {label}
                    {renderSortIndicator(
                      key as "csn" | "name" | "used_users" | "active_seats"
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {sortedRows.map((child, index) => (
              <tr
                key={`${child.name}-${index}`}
                className={`cursor-pointer transition-colors ${
                  index % 2 === 0 ? "bg-background" : "bg-muted/20"
                } hover:bg-primary/10`}
                onClick={() => {
                  const targetName = child.name || "";
                  if (!targetName) return;
                  const target =
                    (corporates ?? []).find(
                      (company) =>
                        company.name === targetName ||
                        company.parent_odoo_name === targetName
                    ) ?? null;
                  if (target) {
                    toggleCorporateSelection(target);
                  }
                }}
              >
                <td className="px-4 py-3 text-muted-foreground align-top">
                  {child.csn || "-"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {child.name || "-"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {child.used_users?.toLocaleString?.() ?? child.used_users ?? "-"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {child.active_seats?.toLocaleString?.() ?? child.active_seats ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <header className="sticky top-12 z-20 flex flex-col gap-4 border-b bg-background/95 pb-4 pt-4 backdrop-blur md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {selectedCorporateName}
          </h1>
          {hasChildren && (
            <p className="text-sm text-muted-foreground">
              Parent accounts and their linked child companies in Autodesk usage.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-6">
          <div className="flex min-w-[320px] flex-col">
            <label className="mb-1 text-xs font-medium text-muted-foreground">
              Companies
            </label>
            <Popover
              open={isCorporatePopoverOpen}
              onOpenChange={setIsCorporatePopoverOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isCorporatePopoverOpen}
                  className={cn(
                    "h-9 w-full justify-between text-sm",
                    isCorporateSelectorDisabled &&
                      "cursor-not-allowed opacity-70"
                  )}
                  disabled={isCorporateSelectorDisabled}
                >
                  {isLoading
                    ? "Loading companies..."
                    : error
                    ? "Failed to load companies"
                    : selectedCorporateCsns.length > 1
                    ? `${selectedCorporateCsns.length} companies selected`
                    : selectedCorporate
                    ? getCorporateLabel(selectedCorporate)
                    : "Select company"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[320px] w-auto p-0" align="end">
                <Command>
                  <CommandInput
                    placeholder="Search company..."
                    className="h-9 text-xs"
                    value={corporateSearchInput}
                    onValueChange={setCorporateSearchInput}
                  />
                  <CommandEmpty>No company found.</CommandEmpty>
                  <div className="max-h-80 overflow-y-auto p-1">
                    {displayedCorporates.map((corporate, index) => {
                      const isSelected = corporate.csn
                        ? selectedCorporateCsns.includes(corporate.csn)
                        : false;
                      const isBlocked =
                        corporate.csn && !isSelected
                          ? isCorporateSelectionBlocked(corporate)
                          : false;
                      const label = getCorporateLabel(corporate);
                      return (
                        <button
                          key={`${corporate.csn ?? "no-csn"}-${index}`}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => toggleCorporateSelection(corporate)}
                          className={cn(
                            "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors",
                            isSelected
                              ? "bg-accent text-accent-foreground"
                              : isBlocked
                              ? "cursor-not-allowed opacity-40"
                              : "hover:bg-accent/60"
                          )}
                          disabled={!corporate.csn || isBlocked}
                        >
                          <div className="flex items-center gap-2">
                            <Checkbox checked={isSelected} />
                            <span className="truncate">{label}</span>
                          </div>
                          <div className="ml-2 flex items-center gap-2">
                            {isSelected ? (
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (corporate.csn) {
                                    removeCorporateSelection(corporate.csn);
                                  }
                                }}
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground"
                                aria-label={`Remove ${label} from selected companies`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ) : null}
                            <span className="text-[11px] text-muted-foreground">
                              {corporate.csn ? `#${corporate.csn}` : "No CSN"}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Command>
                <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    Parent selections lock their descendant companies and remove
                    overlapping picks automatically.
                  </span>
                  {selectedCorporateCsns.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => setSelectedCorporateCsns([])}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>

      {selectedGroupNames.length === 1 ? (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-sm font-semibold tracking-tight">
              Company Account Info
            </CardTitle>
            {accountInfo?.csn &&
              accountInfo?.parentaccountcsn &&
              !csnListIncludes(
                String(accountInfo.csn),
                String(accountInfo.parentaccountcsn)
              ) && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    !findCompanyByCsn(
                      String(accountInfo.parentaccountcsn),
                      corporates ?? []
                    )
                  }
                  onClick={() => {
                    const parentCsn = String(accountInfo.parentaccountcsn);
                    const parent = findCompanyByCsn(parentCsn, corporates ?? []);
                    if (parent) {
                      setSelectedCorporateCsns([parentCsn]);
                      sessionStorage.setItem(
                        "autodesk_usage_selected_corporate_csns",
                        JSON.stringify([parentCsn])
                      );
                      sessionStorage.setItem(
                        "autodesk_usage_selected_corporate_csn",
                        parentCsn
                      );
                    }
                    navigate("/autodesk-usage/corporate");
                  }}
                >
                  Back to Corporate
                </Button>
              )}
          </CardHeader>
          <CardContent className="text-sm">
            {isAccountInfoLoading ? (
              <div className="text-muted-foreground">Loading account info...</div>
            ) : accountInfoError ? (
              <div className="text-destructive">
                {(accountInfoError as any)?.message ||
                  "Failed to load account info."}
              </div>
            ) : accountInfo ? (
              <div className="grid gap-4 md:grid-cols-4">
                {[
                  ["CSN", accountInfo.csn],
                  [
                    "Parent Account",
                    (() => {
                      if (
                        csnListIncludes(
                          String(accountInfo.csn),
                          String(accountInfo.parentaccountcsn ?? "")
                        )
                      ) {
                        return null;
                      }
                      const parentName = accountInfo.parent_name;
                      const parentCsn = accountInfo.parentaccountcsn;
                      if (parentName && parentCsn) {
                        return `${parentName} (${parentCsn})`;
                      }
                      if (parentCsn) {
                        return `- (${parentCsn})`;
                      }
                      return null;
                    })(),
                  ],
                  ["Named Account Group", accountInfo.named_account_group],
                  ["Industry Subsegment", accountInfo.industrysubsegment],
                  ["Phone", accountInfo.phonenumber],
                  [
                    "Address",
                    [
                      accountInfo.address1,
                      accountInfo.address2,
                      accountInfo.address3,
                      accountInfo.city,
                      accountInfo.country,
                    ]
                      .filter(Boolean)
                      .join(", ") || null,
                  ],
                  ["Website", accountInfo.website],
                ].map(([label, value]) => (
                  <div key={label as string} className="space-y-1">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">
                      {label}
                    </div>
                    <div className="text-sm text-foreground">
                      {value ?? "-"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground">No account info.</div>
            )}
          </CardContent>
        </Card>
      ) : selectedGroupNames.length > 1 ? (
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight">
              Selected companies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="text-muted-foreground">
              Multi-company selection is active. All charts and totals below are
              aggregated across the selected companies.
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedCorporates.map((corporate) => {
                const label = getCorporateLabel(corporate);
                return (
                  <span
                    key={corporate.csn ?? label}
                    className="inline-flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-xs text-foreground"
                  >
                    {label}
                    {corporate.csn ? (
                      <button
                        type="button"
                        onClick={() => removeCorporateSelection(corporate.csn ?? "")}
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground"
                        aria-label={`Remove ${label} from selected companies`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="corporate">Company Info</TabsTrigger>
          {showChildCompaniesTab && (
            <TabsTrigger value="child-companies">Child companies</TabsTrigger>
          )}
        </TabsList>

        {showChildCompaniesTab && (
          <TabsContent value="child-companies" className="space-y-6">
            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">
                  Child companies
                </h2>
                <p className="text-sm text-muted-foreground">
                  Accounts mapped under the selected corporate parent.
                </p>
              </div>

              {!selectedGroupNames.length ? (
                renderNoCorporateSelected()
              ) : isChildrenLoading ? (
                <div className="rounded-2xl border bg-card">
                  <Spinner label="Loading child companies..." />
                </div>
              ) : childrenError ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive shadow-sm">
                  {(childrenError as any)?.message ||
                    "Failed to load child companies."}
                </div>
              ) : hasChildren ? (
                renderChildrenTable(children ?? [])
              ) : null}
            </section>
          </TabsContent>
        )}

      <TabsContent value="corporate" className="space-y-6">
          {!selectedGroupNames.length ? (
            renderNoCorporateSelected()
          ) : !selectedGroupName ? (
            <div className="rounded-lg border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
              No Odoo group name is available for this corporate.
            </div>
          ) : (
            <div className="flex flex-col gap-4 md:flex-row md:items-start">
              {hasChildren && (
                <Card className="bg-muted/30 border-transparent shadow-sm md:w-1/4 md:flex-none">
                  <CardHeader className="pb-2 px-4 pt-3">
                    <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Corporate Active Products
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 pt-0">
                    {isProductUsageLoading ? (
                      <Spinner label="Loading corporate product usage..." />
                    ) : productUsageError ? (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive shadow-sm">
                        {(productUsageError as any)?.message ||
                          "Failed to load product usage."}
                      </div>
                    ) : (productUsage ?? []).length === 0 ? (
                      <div className="rounded-lg border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                        No product usage found for this corporate.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {(productUsage ?? []).map((item) => (
                          <div
                            key={`${item.usage_month}-${item.product}`}
                            className="flex items-start justify-between gap-2 text-[12px]"
                          >
                            <span className="text-foreground break-words">
                              {item.product}
                            </span>
                            <span className="text-muted-foreground shrink-0 whitespace-nowrap">
                              {item.used_users} ({item.active_seats})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card className="bg-muted/30 border-transparent shadow-sm md:w-1/4 md:flex-none">
                <CardHeader className="pb-2 px-4 pt-3">
                  <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Company Active Products
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  {selectedGroupNames.length ? (
                    isCompanyProductUsageLoading ? (
                      <Spinner label="Loading company product usage..." />
                    ) : companyProductUsageError ? (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive shadow-sm">
                        {(companyProductUsageError as any)?.message ||
                          "Failed to load company product usage."}
                      </div>
                    ) : (companyProductUsage ?? []).length === 0 ? (
                      <div className="rounded-lg border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                        No company product usage found for the selected companies.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {(companyProductUsage ?? []).map((item) => (
                          <div
                            key={`${item.usage_month}-${item.product}`}
                            className="flex items-start justify-between gap-2 text-[12px]"
                          >
                            <span className="text-foreground break-words">
                              {item.product}
                            </span>
                            <span className="text-muted-foreground shrink-0 whitespace-nowrap">
                              {item.used_users} ({item.active_seats})
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                  ) : null}
                </CardContent>
              </Card>

              {(hasChildren || selectedCorporate) && (
                <div className="flex flex-col gap-4 md:w-1/8 md:flex-none">
                  {hasChildren && (
                    <Card className="bg-muted/30 border-transparent shadow-sm">
                      <CardHeader className="pb-2 px-4 pt-3">
                        <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Corporate Total Seats
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-4 pb-3 pt-0">
                        {isProductUsageLoading ? (
                          <Spinner label="Loading corporate totals..." />
                        ) : productUsageError ? (
                          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive shadow-sm">
                            {(productUsageError as any)?.message ||
                              "Failed to load product usage."}
                          </div>
                        ) : (productUsage ?? []).length === 0 ? (
                          <div className="rounded-lg border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                            No product usage found for this corporate.
                          </div>
                        ) : (
                          <>
                            <div className="text-3xl font-semibold tracking-tight">
                              {(productUsage ?? []).reduce(
                                (sum, item) => sum + (item.used_users || 0),
                                0
                              )}{" "}
                              <span className="text-muted-foreground font-medium">
                                (
                                {(productUsage ?? []).reduce(
                                  (sum, item) =>
                                    sum + (item.active_seats || 0),
                                  0
                                )}
                                )
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              total used users (active seats)
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  <Card className="bg-muted/30 border-transparent shadow-sm">
                    <CardHeader className="pb-2 px-4 pt-3">
                      <CardTitle className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Company Total Seats
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 pb-3 pt-0">
                      {selectedGroupNames.length ? (
                        isCompanyProductUsageLoading ? (
                          <Spinner label="Loading company totals..." />
                        ) : companyProductUsageError ? (
                          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive shadow-sm">
                            {(companyProductUsageError as any)?.message ||
                              "Failed to load company totals."}
                          </div>
                        ) : (companyProductUsage ?? []).length === 0 ? (
                          <div className="rounded-lg border bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                            No company product usage found for the selected companies.
                          </div>
                        ) : (
                          <>
                            <div className="text-3xl font-semibold tracking-tight">
                              {(companyProductUsage ?? []).reduce(
                                (sum, item) => sum + (item.used_users || 0),
                                0
                              )}{" "}
                              <span className="text-muted-foreground font-medium">
                                (
                                {(companyProductUsage ?? []).reduce(
                                  (sum, item) => sum + (item.active_seats || 0),
                                  0
                                )}
                                )
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              total used users (active seats)
                            </div>
                          </>
                        )
                      ) : null}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {selectedGroupNames.length > 0 && (
            <>
              <section className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight">
                      Active licenses
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      One selector controls both charts. Yearly, quarterly,
                      monthly, and custom month ranges all use the same month
                      boundary logic.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="flex flex-col gap-1 text-xs font-medium text-slate-500 sm:min-w-[220px]">
                      Time grouping
                      <Select
                        value={timeMode}
                        onValueChange={(value) => setTimeMode(value as UsageMode)}
                      >
                        <SelectTrigger className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500">
                          <SelectValue placeholder="Select grouping" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yearly">Yearly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="date_range">Date range</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                  </div>
                </div>

                {timeMode === "date_range" ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                      Start month
                      <input
                        type="month"
                        value={customStartMonth}
                        onChange={(e) => setCustomStartMonth(e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
                      End month
                      <input
                        type="month"
                        value={customEndMonth}
                        onChange={(e) => setCustomEndMonth(e.target.value)}
                        className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-blue-500"
                      />
                    </label>
                  </div>
                ) : null}

                <div className="grid gap-4 xl:grid-cols-2">
                  <CorporateMonthlyUsageChart
                    corporateName={selectedGroupNames}
                    mode={timeMode}
                    startMonth={chartRange.startMonth}
                    endMonth={chartRange.endMonth}
                  />
                  <CompanyMonthlyUsageChart
                    companyName={selectedGroupNames}
                    mode={timeMode}
                    startMonth={chartRange.startMonth}
                    endMonth={chartRange.endMonth}
                  />
                </div>
              </section>

            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}



